import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const navItems = [
    { label: "Home", href: "/" },
    { label: "Team", href: "/team" },
];

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && 
                menuRef.current && 
                !menuRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        const handleTouchStart = (event) => {
            if (isMenuOpen && 
                menuRef.current && 
                !menuRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        // Add both mouse and touch event listeners
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleTouchStart);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleTouchStart);
        };
    }, [isMenuOpen]);

    // Close menu on escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape' && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isMenuOpen]);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    return (
        <>
            <div className="fixed top-0 left-0 right-0 z-50 px-4 py-3 flex justify-center">
                {/* Hide main navbar on mobile, show only on desktop */}
                <nav className={`w-full max-w-6xl mx-auto rounded-full transition-all duration-300 hidden md:block ${
                    scrolled 
                        ? "bg-black/80 backdrop-blur-sm shadow-lg border border-gray-800/50" 
                        : "bg-transparent"
                }`}>
                    <div className="px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-12">
                            <div className="flex-shrink-0">
                                <Link to="/" className={`text-xl font-bold transition-all duration-300 ${
                                    scrolled ? 'text-[hsl(30,100%,85%)]' : 'text-[hsl(30,100%,85%)]'
                                } hover:text-white`}>
                                    Inika
                                </Link>
                            </div>

                            {/* Desktop Navigation */}
                            <div className="hidden md:block">
                                <div className="ml-10 flex items-center space-x-8">
                                    {navItems.map((item) => (
                                        <Link
                                            key={item.label}
                                            to={item.href}
                                            className={`transition-all duration-200 text-sm font-medium relative group ${
                                                scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-300 hover:text-white'
                                            }`}
                                        >
                                            {item.label}
                                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[hsl(30,100%,85%)] to-white transition-all duration-300 group-hover:w-full"></span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Mobile Logo - visible on mobile */}
                <div className="md:hidden fixed top-4 left-4">
                    <Link to="/" className="text-xl font-bold text-[hsl(30,100%,85%)] hover:text-white transition-colors">
                        Inika
                    </Link>
                </div>

                {/* Mobile menu button - always visible on mobile */}
                <div className="md:hidden fixed top-4 right-4">
                    <button
                        ref={buttonRef}
                        type="button"
                        className={`inline-flex items-center justify-center p-2 rounded-full transition-all duration-200 focus:outline-none touch-manipulation ${
                            scrolled 
                                ? 'text-gray-300 hover:text-white hover:bg-gray-800/30' 
                                : 'text-gray-300 hover:text-white hover:bg-gray-800/30'
                        } ${isMenuOpen ? 'bg-gray-800/50' : ''}`}
                        aria-controls="mobile-menu"
                        aria-expanded={isMenuOpen}
                        onClick={toggleMenu}
                    >
                        <span className="sr-only">{isMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
                        {/* Hamburger icon with animation */}
                        <div className="w-6 h-6 flex flex-col justify-center items-center">
                            <span className={`bg-current block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                                isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'
                            }`}></span>
                            <span className={`bg-current block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${
                                isMenuOpen ? 'opacity-0' : 'opacity-100'
                            }`}></span>
                            <span className={`bg-current block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                                isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'
                            }`}></span>
                        </div>
                    </button>
                </div>
            </div>

            {/* Mobile Navigation Overlay */}
            {isMenuOpen && (
                <div className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300">
                    {/* Mobile Navigation Menu */}
                    <div 
                        ref={menuRef}
                        className={`fixed top-0 right-0 h-full w-72 transform transition-transform duration-300 ease-in-out ${
                            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                        }`}
                    >
                        <div className="h-full px-6 pt-20 pb-6 bg-gray-900/95 backdrop-blur-md border-l border-gray-800/50 shadow-2xl">
                            <div className="space-y-6">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.label}
                                        to={item.href}
                                        className="block px-4 py-3 rounded-lg text-lg font-medium text-gray-300 hover:text-white hover:bg-gray-800/50 transition-all duration-200 touch-manipulation"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                            
                            {/* Close instruction */}
                            <div className="absolute bottom-6 left-6 right-6">
                                <p className="text-xs text-gray-500 text-center">
                                    Tap outside to close
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar; 